import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Checkbox, Empty, Input, InputNumber, message, Modal, Select } from 'antd'
import {
    createPlans, deletePlan, patchPlan, requestPlannerActivities, requestPlans,
    requestWorkouts, setActiveActivityRange, setActivePlanRange,
} from './store/store'
import { activityRangeKey, planRangeKey, selectPlanMutation } from './store/plans'
import { useWorkoutFavorites } from './useWorkoutFavorites'
import { useLocalToday } from './useLocalToday'
import WorkoutProfileThumbnail from './WorkoutProfileThumbnail'
import {
    DURATION_PRESETS, INTENSITY_PRESETS, LOAD_PRESETS, SORT_OPTIONS, TYPE_OPTIONS,
    WEEKDAYS_SHORT, ZONES, addDays, addMonths, aggregateLoads, computeRepeat, dayLoads,
    dowMon, filterPlannerWorkouts, firstOfMonth, fmtDelta, fmtDur, fmtInt, fmtLoad,
    fmtLong, fmtMonthTitle, fmtShort, isValidDate, monthGridDates, monthRange,
    monthWeeks, normalizeActivity, parseLocal, sortPlannerWorkouts, todayLocal,
    workoutTypeLabel,
} from './plannerLogic'
import './css/planner.css'

const PAGE = 16

const raf = (typeof window !== 'undefined' && window.requestAnimationFrame)
    ? window.requestAnimationFrame.bind(window)
    : cb => setTimeout(cb, 0)

function defaultFilters() {
    return {
        query: '',
        type: 'all',
        sort: 'loadAsc',
        favOnly: false,
        durationPreset: 'any',
        intensityPreset: 'any',
        loadPreset: 'any',
    }
}

function localMidnightEpoch(dateStr) {
    return Math.floor(parseLocal(dateStr).getTime() / 1000)
}

const ZONE_LABELS = Object.fromEntries(ZONES)

function LoadLine({ loads }) {
    const planV = loads.planN > 0 ? fmtLoad(loads.plan) : '—'
    const planUnknownMark = loads.planUnknown > 0 ? ` +${loads.planUnknown} неизв.` : ''
    let factV
    if (loads.actN === 0) {
        factV = '—'
    } else if (loads.unknown > 0) {
        factV = (loads.fact > 0 ? `${fmtLoad(loads.fact)} +` : '') + 'неизв.'
    } else {
        factV = fmtLoad(loads.fact)
    }
    const showDelta = loads.planN > 0 && loads.actN > 0
        && loads.planUnknown === 0 && loads.unknown === 0
        && Math.abs(loads.fact - loads.plan) >= 0.05
    return (
        <div className="planner-loads">
            <span className="planner-l-plan">План <b>{planV}</b>{planUnknownMark}</span>
            <span className="planner-l-fact">Факт <b>{factV}</b></span>
            {showDelta && <span className="planner-delta">{fmtDelta(loads.fact - loads.plan)}</span>}
        </div>
    )
}

export default function ViewPlanner({ onNavigate }) {
    const today = useLocalToday()
    const { favoriteIds, toggleFavorite } = useWorkoutFavorites()
    const workouts = useSelector(state => state.workouts.workouts)
    const createStatus = useSelector(state => state.plans.createStatus)
    const createBusy = createStatus === 'pending'

    const [viewMonth, setViewMonth] = useState(() => firstOfMonth(today))
    const [narrow, setNarrow] = useState(false)
    const rootRef = useRef(null)

    const [pickerOpen, setPickerOpen] = useState(false)
    const [pickerDate, setPickerDate] = useState(null)
    const [pickerFilters, setPickerFilters] = useState(defaultFilters)
    const [pickerPage, setPickerPage] = useState(0)
    const resultsRef = useRef(null)
    const pickerScrollTopRef = useRef(0)

    const [detailsDate, setDetailsDate] = useState(null)
    const [editorOpen, setEditorOpen] = useState(false)
    const [editorPlan, setEditorPlan] = useState(null)
    const [moveDate, setMoveDate] = useState('')
    const [dupDate, setDupDate] = useState('')
    const [repeatOpen, setRepeatOpen] = useState(false)
    const [repeatWeekdays, setRepeatWeekdays] = useState([])
    const [repeatWeeks, setRepeatWeeks] = useState('4')
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [editorError, setEditorError] = useState(null)

    const openerRef = useRef(null)

    const gridRange = useMemo(() => monthRange(viewMonth), [viewMonth])
    const gridDates = useMemo(() => monthGridDates(viewMonth), [viewMonth])
    const planKey = gridRange ? planRangeKey(gridRange.dateFrom, gridRange.dateTo) : null
    const planRange = useSelector(state => (planKey ? state.plans.ranges[planKey] : null))

    const activityRange = useMemo(() => {
        if (!gridDates.length) return null
        return {
            startFrom: localMidnightEpoch(gridDates[0]),
            startBefore: localMidnightEpoch(addDays(gridDates[gridDates.length - 1], 1)),
        }
    }, [gridDates])
    const activityKey = activityRange ? activityRangeKey(activityRange.startFrom, activityRange.startBefore) : null
    const activityRangeState = useSelector(state => (activityKey ? state.plans.activityRanges[activityKey] : null))

    const planMutation = useSelector(state => (editorPlan ? selectPlanMutation(state, editorPlan.id) : undefined))
    const mutationBusy = Boolean(planMutation && planMutation.status === 'pending')

    useEffect(() => {
        if (!gridRange) return
        setActivePlanRange(gridRange)
        requestPlans(gridRange)
    }, [gridRange])

    useEffect(() => {
        if (!activityRange) return
        setActiveActivityRange(activityRange)
        requestPlannerActivities(activityRange)
    }, [activityRange])

    useEffect(() => {
        requestWorkouts()
    }, [])

    const refreshVisible = useCallback(() => {
        if (gridRange) requestPlans(gridRange)
        if (activityRange) requestPlannerActivities(activityRange)
    }, [gridRange, activityRange])

    useEffect(() => {
        const onFocus = () => refreshVisible()
        const onVisibility = () => {
            if (document.visibilityState === 'visible') refreshVisible()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [refreshVisible])

    useEffect(() => {
        const el = rootRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const update = () => setNarrow(el.clientWidth < 820)
        const observer = new ResizeObserver(update)
        observer.observe(el)
        update()
        return () => observer.disconnect()
    }, [])

    const workoutsById = useMemo(() => {
        const map = {}
        for (const w of workouts || []) map[w.id] = w
        return map
    }, [workouts])

    const plannedRows = planRange ? planRange.rows : []
    const plansByDate = useMemo(() => {
        const map = {}
        for (const p of plannedRows) (map[p.date] = map[p.date] || []).push(p)
        return map
    }, [plannedRows])

    const normalizedActivities = useMemo(() => {
        const rows = activityRangeState ? activityRangeState.rows : []
        return rows.map(a => normalizeActivity(a, workoutsById)).filter(Boolean)
    }, [activityRangeState, workoutsById])

    const actsByDate = useMemo(() => {
        const map = {}
        for (const a of normalizedActivities) (map[a.date] = map[a.date] || []).push(a)
        return map
    }, [normalizedActivities])

    const rememberOpener = () => {
        openerRef.current = document.activeElement
    }

    const restoreOpenerFocus = () => {
        if (openerRef.current && document.contains(openerRef.current)) openerRef.current.focus()
    }

    // --- picker -------------------------------------------------------------

    const openPicker = date => {
        rememberOpener()
        setPickerDate(date)
        setPickerOpen(true)
        raf(() => {
            if (resultsRef.current) resultsRef.current.scrollTop = pickerScrollTopRef.current
        })
    }

    const closePicker = () => {
        setPickerOpen(false)
    }

    const onPickerScroll = () => {
        if (resultsRef.current) pickerScrollTopRef.current = resultsRef.current.scrollTop
    }

    const updateFilter = patch => {
        setPickerFilters(filters => ({ ...filters, ...patch }))
        setPickerPage(0)
    }

    const resetPickerFilters = () => {
        setPickerFilters(defaultFilters())
        setPickerPage(0)
    }

    const filteredWorkouts = useMemo(() => {
        const list = filterPlannerWorkouts(workouts || [], {
            query: pickerFilters.query,
            type: pickerFilters.type,
            favOnly: pickerFilters.favOnly,
            favoriteIds,
            durationPreset: pickerFilters.durationPreset,
            intensityPreset: pickerFilters.intensityPreset,
            loadPreset: pickerFilters.loadPreset,
        })
        return sortPlannerWorkouts(list, pickerFilters.sort)
    }, [workouts, pickerFilters, favoriteIds])

    const visibleWorkouts = filteredWorkouts.slice(0, (pickerPage + 1) * PAGE)

    const addFromPicker = async workout => {
        if (createBusy) return
        try {
            await createPlans([{ workout_id: workout.id, date: pickerDate }])
            message.success(`Добавлено: ${workout.name} на ${fmtShort(pickerDate)}`)
        } catch (e) {
            message.error((e && e.message) || 'Не удалось добавить тренировку')
        }
    }

    // --- details ------------------------------------------------------------

    const openDetails = date => {
        rememberOpener()
        setDetailsDate(date)
    }

    const openCatalogueForDate = date => {
        if (!onNavigate) return
        const params = new URLSearchParams()
        params.set('category', 'planned')
        params.set('date', date)
        if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
        }
        onNavigate('workouts')
    }

    const openEditorFromDetails = plan => {
        setDetailsDate(null)
        openEditor(plan)
    }

    // --- editor -------------------------------------------------------------

    const openEditor = plan => {
        rememberOpener()
        setEditorPlan(plan)
        setMoveDate(plan.date)
        setDupDate(plan.date)
        setRepeatOpen(false)
        setRepeatWeekdays([dowMon(plan.date)])
        setRepeatWeeks('4')
        setConfirmRemove(false)
        setEditorError(null)
        setEditorOpen(true)
    }

    const closeEditor = () => {
        setEditorOpen(false)
    }

    const doMove = async () => {
        if (!editorPlan) return
        if (!isValidDate(moveDate)) {
            setEditorError('Укажите корректную дату для переноса.')
            return
        }
        try {
            await patchPlan(editorPlan.id, moveDate)
            message.success(`Перенесено на ${fmtShort(moveDate)}`)
            setEditorOpen(false)
        } catch (e) {
            setEditorError((e && e.message) || 'Не удалось перенести тренировку')
        }
    }

    const doDuplicate = async () => {
        if (!editorPlan) return
        if (!isValidDate(dupDate)) {
            setEditorError('Укажите корректную дату для дублирования.')
            return
        }
        try {
            await createPlans([{ workout_id: editorPlan.workout_id, date: dupDate }])
            message.success(`Дублировано на ${fmtShort(dupDate)}`)
            setEditorOpen(false)
        } catch (e) {
            setEditorError((e && e.message) || 'Не удалось дублировать тренировку')
        }
    }

    const doRemove = async () => {
        if (!editorPlan) return
        if (!confirmRemove) {
            setConfirmRemove(true)
            return
        }
        try {
            await deletePlan(editorPlan.id)
            message.success('Тренировка удалена')
            setEditorOpen(false)
        } catch (e) {
            setEditorError((e && e.message) || 'Не удалось удалить тренировку')
        }
    }

    const repeatResult = useMemo(() => {
        if (!editorPlan || !repeatOpen) return null
        return computeRepeat(editorPlan.date, repeatWeekdays, repeatWeeks)
    }, [editorPlan, repeatOpen, repeatWeekdays, repeatWeeks])

    const repeatPreviewText = useMemo(() => {
        if (!repeatResult || !repeatResult.ok) return ''
        const tss = editorPlan.workout && editorPlan.workout.tss
        const addedLoad = tss == null ? null : repeatResult.dates.length * tss
        const first = repeatResult.dates.slice(0, 8).map(fmtShort).join(', ')
        const more = repeatResult.dates.length > 8 ? `, … и ещё ${repeatResult.dates.length - 8}` : ''
        return `До ${fmtLong(repeatResult.end)} включительно · Даты: ${first}${more} · всего ${repeatResult.dates.length} · добавленная нагрузка ${addedLoad == null ? 'неизвестна' : fmtLoad(addedLoad)}`
    }, [repeatResult, editorPlan])

    const applyRepeat = async () => {
        if (!repeatResult || !repeatResult.ok || createBusy) return
        const items = repeatResult.dates.map(d => ({ workout_id: editorPlan.workout_id, date: d }))
        try {
            await createPlans(items)
            message.success(`Повтор применён: ${repeatResult.dates.length} тренировок`)
            setRepeatOpen(false)
            setRepeatWeekdays([])
            setRepeatWeeks('4')
        } catch (e) {
            setEditorError((e && e.message) || 'Не удалось применить повтор')
        }
    }

    // --- rendering helpers --------------------------------------------------

    const monthPrefix = viewMonth.slice(0, 7)
    const pickerLoads = pickerDate ? dayLoads(plansByDate[pickerDate] || [], actsByDate[pickerDate] || []) : null
    const detailsLoads = detailsDate ? dayLoads(plansByDate[detailsDate] || [], actsByDate[detailsDate] || []) : null
    const editorWorkout = editorPlan && editorPlan.workout
    const intensityHint = (INTENSITY_PRESETS.find(p => p.id === pickerFilters.intensityPreset) || {}).hint

    const renderDayCell = date => {
        const dayNumber = parseLocal(date).getDate()
        const planned = plansByDate[date] || []
        const acts = actsByDate[date] || []
        const loads = dayLoads(planned, acts)
        const adjacent = date.slice(0, 7) !== monthPrefix
        const isToday = date === today
        const classes = ['planner-daycell']
        if (isToday) classes.push('is-today')
        if (adjacent) classes.push('is-adjacent')
        const monthAbbr = adjacent ? fmtShort(date).split(' ')[1].slice(0, 3) : ''

        const stop = e => e.stopPropagation()
        return (
            <div
                className={classes.join(' ')}
                key={date}
                data-testid={date}
                data-date={date}
                role="button"
                tabIndex={0}
                aria-label={`${fmtLong(date)}, добавить тренировку`}
                onClick={() => openPicker(date)}
                onKeyDown={e => {
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openPicker(date)
                    }
                }}
            >
                <div className="planner-daycell-head">
                    <button
                        type="button"
                        className="planner-daynum"
                        onClick={e => {
                            stop(e)
                            openPicker(date)
                        }}
                    >
                        <span className="planner-daynum-value">{dayNumber}</span>
                        <span className="planner-dayweek">{WEEKDAYS_SHORT[dowMon(date)]}</span>
                        {adjacent && <span className="planner-daymonth">{monthAbbr}</span>}
                    </button>
                </div>
                {planned.slice(0, 2).map(p => (
                    <button
                        key={p.id}
                        type="button"
                        className="planner-chip planner-chip-plan"
                        onClick={e => {
                            stop(e)
                            openEditor(p)
                        }}
                        title={`${p.workout.name} · нагрузка ${fmtLoad(p.workout.tss)}`}
                    >
                        {p.workout.name} <b>{fmtLoad(p.workout.tss)}</b>
                    </button>
                ))}
                {planned.length > 2 && (
                    <button
                        type="button"
                        className="planner-chip planner-chip-more"
                        onClick={e => {
                            stop(e)
                            openDetails(date)
                        }}
                    >
                        Ещё {planned.length - 2}
                    </button>
                )}
                {planned.length > 0 && (
                    <button
                        type="button"
                        className="planner-catalogue-link"
                        onClick={e => {
                            stop(e)
                            openCatalogueForDate(date)
                        }}
                    >
                        в каталоге
                    </button>
                )}
                {acts.slice(0, 1).map((a, i) => (
                    <span key={`act-${i}`} className="planner-chip planner-chip-act" title={`${a.title} · нагрузка ${a.unknown ? 'неизвестна' : fmtLoad(a.load)}`}>
                        {a.title} <b>{a.unknown ? '—' : fmtLoad(a.load)}</b>
                    </span>
                ))}
                {acts.length > 1 && (
                    <button
                        type="button"
                        className="planner-chip planner-chip-more"
                        onClick={e => {
                            stop(e)
                            openDetails(date)
                        }}
                    >
                        Ещё {acts.length - 1}
                    </button>
                )}
                <LoadLine loads={loads} />
            </div>
        )
    }

    const renderWeekSummary = week => {
        const total = aggregateLoads(week.map(date => dayLoads(plansByDate[date] || [], actsByDate[date] || [])))
        const factV = total.actN === 0
            ? '—'
            : (total.unknown > 0 ? (total.fact > 0 ? `${fmtLoad(total.fact)} +` : '') + 'неизв.' : fmtLoad(total.fact))
        return (
            <div className="planner-weeksum" key={`sum-${week[0]}`}>
                <div className="planner-weeksum-range">{fmtShort(week[0])} — {fmtShort(week[6])}</div>
                <div className="planner-weeksum-line">План <b>{total.planN > 0 ? fmtLoad(total.plan) : '—'}</b></div>
                <div className="planner-weeksum-line is-fact">Факт <b>{factV}</b></div>
                {total.unknown > 0 && <div className="planner-weeksum-note">+{total.unknown} без данных мощности</div>}
            </div>
        )
    }

    const daysDetailsPlans = detailsDate ? (plansByDate[detailsDate] || []) : []
    const daysDetailsActs = detailsDate ? (actsByDate[detailsDate] || []) : []

    return (
        <main className="planner-root" ref={rootRef}>
            <div className={`planner-inner${narrow ? ' is-narrow' : ''}`}>
                <header className="planner-topbar">
                    <button type="button" className="planner-nav-btn" aria-label="Предыдущий месяц" onClick={() => setViewMonth(addMonths(viewMonth, -1))}>‹</button>
                    <h1 className="planner-month-title">{fmtMonthTitle(viewMonth)}</h1>
                    <button type="button" className="planner-nav-btn" aria-label="Следующий месяц" onClick={() => setViewMonth(addMonths(viewMonth, 1))}>›</button>
                    <Button onClick={() => setViewMonth(firstOfMonth(today))}>Сегодня</Button>
                </header>

                {planRange && planRange.status === 'error' && (
                    <div className="planner-banner is-error" role="alert">
                        Не удалось загрузить план: {(planRange.error && planRange.error.message) || 'ошибка сети'}
                    </div>
                )}
                {planRange && planRange.stale && (
                    <div className="planner-banner" role="status">Данные устарели — обновление не удалось. Показаны последние загруженные.</div>
                )}

                <div className="planner-weekday-row">
                    {WEEKDAYS_SHORT.map(d => <div className="planner-wd-head" key={d}>{d}</div>)}
                    <div className="planner-wd-head">Итог недели</div>
                </div>
                {monthWeeks(viewMonth).map(week => (
                    <div className="planner-weekrow" key={week[0]}>
                        {week.map(renderDayCell)}
                        {renderWeekSummary(week)}
                    </div>
                ))}
            </div>

            <Modal
                className="planner-modal"
                title={<span>Добавить тренировку на <span className="planner-head-date">{pickerDate ? fmtLong(pickerDate) : ''}</span></span>}
                visible={pickerOpen}
                onCancel={closePicker}
                onOk={closePicker}
                okText="Закрыть"
                width={920}
            >
                {pickerLoads && (
                    <p className="planner-picker-sub">
                        Уже запланировано: <b>{pickerLoads.planN}</b>, План <b>{fmtLoad(pickerLoads.plan)}</b> · Факт <b>{pickerLoads.actN > 0 ? fmtLoad(pickerLoads.fact) : '—'}</b>
                        {pickerLoads.unknown > 0 ? ` +${pickerLoads.unknown} неизв.` : ''}
                    </p>
                )}
                <div className="planner-filters">
                    <div className="planner-filters-row">
                        <Input.Search
                            placeholder="Поиск по названию"
                            value={pickerFilters.query}
                            onChange={e => updateFilter({ query: e.target.value })}
                            allowClear
                            style={{ width: 200 }}
                        />
                        <Select
                            value={pickerFilters.type}
                            onChange={value => updateFilter({ type: value })}
                            options={TYPE_OPTIONS}
                            style={{ width: 150 }}
                        />
                        <Select
                            value={pickerFilters.sort}
                            onChange={value => updateFilter({ sort: value })}
                            options={SORT_OPTIONS}
                            style={{ width: 180 }}
                        />
                        <Checkbox
                            checked={pickerFilters.favOnly}
                            onChange={e => updateFilter({ favOnly: e.target.checked })}
                        >
                            Только избранные
                        </Checkbox>
                        <Button onClick={resetPickerFilters}>Сбросить фильтры</Button>
                    </div>
                    <div className="planner-presets-row">
                        <Select
                            value={pickerFilters.durationPreset}
                            onChange={value => updateFilter({ durationPreset: value })}
                            options={DURATION_PRESETS.map(p => ({ value: p.id, label: p.label }))}
                        />
                        <Select
                            value={pickerFilters.intensityPreset}
                            onChange={value => updateFilter({ intensityPreset: value })}
                            options={INTENSITY_PRESETS.map(p => ({ value: p.id, label: p.label, title: p.hint || undefined }))}
                            title={intensityHint || undefined}
                        />
                        <Select
                            value={pickerFilters.loadPreset}
                            onChange={value => updateFilter({ loadPreset: value })}
                            options={LOAD_PRESETS.map(p => ({ value: p.id, label: p.label }))}
                        />
                    </div>
                </div>
                <div className="planner-results-wrap">
                    <div className="planner-count">
                        Найдено: {filteredWorkouts.length} из {(workouts || []).length}
                    </div>
                    <div
                        className="planner-results"
                        tabIndex={-1}
                        aria-label="Результаты поиска тренировок"
                        ref={resultsRef}
                        onScroll={onPickerScroll}
                    >
                        {visibleWorkouts.map(workout => (
                            <div className="planner-result" key={workout.id}>
                                <span className="planner-res-profile">
                                    <WorkoutProfileThumbnail points={workout.points} />
                                </span>
                                <button
                                    type="button"
                                    className="planner-res-main"
                                    disabled={createBusy}
                                    onClick={() => addFromPicker(workout)}
                                    aria-label={`Добавить «${workout.name}» на выбранную дату`}
                                >
                                    <b>{workout.name}</b>
                                    <span className="planner-res-meta">
                                        {workoutTypeLabel(workout) || '—'} · {fmtDur(workout.duration_s)} · инт. {fmtInt(workout.if_)}
                                    </span>
                                </button>
                                <span className="planner-res-load">Нагрузка<b>{fmtLoad(workout.tss)}</b></span>
                                <button
                                    type="button"
                                    className={`planner-res-fav${favoriteIds.includes(workout.id) ? ' is-on' : ''}`}
                                    aria-pressed={favoriteIds.includes(workout.id)}
                                    aria-label={favoriteIds.includes(workout.id) ? 'Убрать из избранного' : 'В избранное'}
                                    onClick={() => toggleFavorite(workout.id)}
                                >
                                    {favoriteIds.includes(workout.id) ? '★' : '☆'}
                                </button>
                            </div>
                        ))}
                        {!visibleWorkouts.length && <Empty description="Ничего не найдено. Измените фильтры." />}
                    </div>
                    {filteredWorkouts.length > visibleWorkouts.length && (
                        <Button className="planner-more-btn" onClick={() => setPickerPage(page => page + 1)}>
                            Показать ещё ({filteredWorkouts.length - visibleWorkouts.length})
                        </Button>
                    )}
                </div>
            </Modal>

            <Modal
                className="planner-modal"
                title={`День, ${detailsDate ? fmtLong(detailsDate) : ''}`}
                visible={Boolean(detailsDate)}
                onCancel={() => setDetailsDate(null)}
                footer={null}
                width={420}
            >
                {detailsLoads && <LoadLine loads={detailsLoads} />}
                <h3 className="planner-detail-h">План</h3>
                {detailsDate && (
                    <button type="button" className="planner-catalogue-link" onClick={() => openCatalogueForDate(detailsDate)}>
                        Открыть в каталоге
                    </button>
                )}
                <div className="planner-dd-list">
                    {daysDetailsPlans.map(p => {
                        const catalogueWorkout = workoutsById[p.workout_id]
                        const typeLabel = catalogueWorkout ? workoutTypeLabel(catalogueWorkout) : null
                        return (
                            <button
                                key={p.id}
                                type="button"
                                className="planner-occ-card"
                                onClick={() => openEditorFromDetails(p)}
                            >
                                <b>{p.workout.name}</b>
                                <span>{typeLabel || '—'} · {fmtDur(p.workout.duration_s)} · инт. {fmtInt(p.workout.if_)}</span>
                                <span className="planner-occ-load">{fmtLoad(p.workout.tss)}</span>
                            </button>
                        )
                    })}
                    {!daysDetailsPlans.length && <p className="planner-detail-empty">Плана нет.</p>}
                </div>
                <h3 className="planner-detail-h">Факт</h3>
                <div className="planner-dd-list">
                    {daysDetailsActs.map((a, i) => (
                        <div className="planner-act-card" key={`act-${i}`}>
                            <b>{a.title}</b>
                            <span>{a.typeKey ? ZONE_LABELS[a.typeKey] : '—'} · {fmtDur(a.duration)}</span>
                            <span className="planner-act-load">{a.unknown ? '—' : fmtLoad(a.load)}</span>
                        </div>
                    ))}
                    {!daysDetailsActs.length && <p className="planner-detail-empty">Нет записанных активностей.</p>}
                </div>
            </Modal>

            <Modal
                className="planner-modal"
                title="Тренировка"
                visible={editorOpen}
                onCancel={closeEditor}
                footer={null}
                width={460}
            >
                {editorWorkout && (
                    <p className="planner-oe-info">
                        <b>{editorWorkout.name}</b><br />
                        {fmtLong(editorPlan.date)} · нагрузка {fmtLoad(editorWorkout.tss)}
                    </p>
                )}
                <div className="planner-oe-row">
                    <label htmlFor="planner-move-date">Перенести на дату</label>
                    <Input type="date" id="planner-move-date" value={moveDate} onChange={e => setMoveDate(e.target.value)} style={{ width: 170 }} />
                    <Button onClick={doMove} disabled={mutationBusy}>Перенести</Button>
                </div>
                <div className="planner-oe-row">
                    <label htmlFor="planner-dup-date">Дублировать на дату</label>
                    <Input type="date" id="planner-dup-date" value={dupDate} onChange={e => setDupDate(e.target.value)} style={{ width: 170 }} />
                    <Button onClick={doDuplicate} disabled={createBusy}>Дублировать</Button>
                </div>
                <div className="planner-oe-row">
                    <Checkbox checked={repeatOpen} onChange={e => setRepeatOpen(e.target.checked)}>Повторять</Checkbox>
                </div>
                {repeatOpen && (
                    <div className="planner-oe-repeat">
                        <fieldset className="planner-oe-weekdays">
                            <legend>Дни недели</legend>
                            {WEEKDAYS_SHORT.map((label, index) => (
                                <label key={label}>
                                    <input
                                        type="checkbox"
                                        checked={repeatWeekdays.includes(index)}
                                        onChange={e => {
                                            const next = e.target.checked
                                                ? [...repeatWeekdays, index].sort((a, b) => a - b)
                                                : repeatWeekdays.filter(w => w !== index)
                                            setRepeatWeekdays(next)
                                        }}
                                    />
                                    {label}
                                </label>
                            ))}
                        </fieldset>
                        <div className="planner-oe-row">
                            <label htmlFor="planner-weeks">Количество недель</label>
                            <InputNumber
                                id="planner-weeks"
                                min={1}
                                max={52}
                                step={1}
                                value={repeatWeeks}
                                onChange={value => setRepeatWeeks(value == null ? '' : String(value))}
                            />
                        </div>
                        <p className="planner-oe-limit">Повтор ограничен 52 неделями от даты начала. Существующая тренировка не дублируется.</p>
                        {repeatResult && !repeatResult.ok && (
                            <p className="planner-oe-error">Повтор не готов: {repeatResult.errors.join('; ')}.</p>
                        )}
                        {repeatPreviewText && <p className="planner-oe-preview">{repeatPreviewText}</p>}
                        <div className="planner-oe-row">
                            <Button type="primary" disabled={!repeatResult || !repeatResult.ok || createBusy} onClick={applyRepeat}>
                                Применить повтор
                            </Button>
                            <Button onClick={() => { setRepeatOpen(false); setRepeatWeekdays([]) }}>Отменить</Button>
                        </div>
                    </div>
                )}
                {editorError && <p className="planner-oe-error" role="alert">{editorError}</p>}
                <Button className="planner-oe-remove" danger onClick={doRemove} disabled={mutationBusy}>
                    {confirmRemove ? 'Точно удалить?' : 'Удалить'}
                </Button>
            </Modal>
        </main>
    )
}